// Replay player (REP-3). Rebuilds a world from a `.bbreplay`'s {seed, options} (or
// its start snapshot) and re-simulates by feeding the recorded `InputFrame`s at
// their ticks — the SAME canonical `ReplaySession` the recorder drove, so the
// re-sim is bit-identical. On every crossed keyframe it asserts the recorded
// `hashWorld` (E-31: a mismatch stops playback and reports the diverging tick — a
// determinism bug, never silently continued). The replay world is READ-ONLY: it is
// never persisted and never accepts live input.
//
// Keyframes in the file are just {tick, hash} (kilobyte-scale). To make seeking
// cheap the player MATERIALIZES an in-memory restore `Snapshot` at each keyframe it
// crosses (coords + edited sections — the same trick the recorder uses), so a
// backward scrub re-sims from the nearest checkpoint ≤ t rather than from tick 0.
// Pure: no DOM, no three.
import type { Registries } from '../registry/load';
import { hashWorld } from '../engine/worldhash';
import { ReplaySession } from './session';
import { parseReplay, type Replay, type Snapshot } from './format';

/** Thrown when a re-sim keyframe hash does not match the recording (E-31). */
export class ReplayDriftError extends Error {
  constructor(readonly tick: number, readonly expected: string, readonly actual: string) {
    super(`replay drift at tick ${tick}: expected ${expected}, got ${actual} — a determinism bug`);
    this.name = 'ReplayDriftError';
  }
}

export class ReplayPlayer {
  private readonly replay: Replay;
  private readonly keyByTick: Map<number, string>;
  private readonly checkpoints = new Map<number, Snapshot>(); // in-memory restore points
  private session: ReplaySession;
  private head: number; // absolute tick of the current state

  constructor(private readonly reg: Registries, replay: Replay) {
    this.replay = parseReplay(replay); // E-33 validate on load (never trust the blob)
    this.keyByTick = new Map(this.replay.keyframes.map((k) => [k.tick, k.hash]));
    this.session = this.buildStart();
    this.head = this.replay.startTick;
    this.checkpoints.set(this.head, this.session.snapshot());
    this.assertKeyframe(this.head); // the start tick is a keyframe — verify it
  }

  /** First tick of the recording. */
  get startTick(): number {
    return this.replay.startTick;
  }

  /** One past the last tick (startTick + frame count). */
  get endTick(): number {
    return this.replay.startTick + this.replay.frames.length;
  }

  /** Absolute tick of the state currently shown. */
  get tick(): number {
    return this.head;
  }

  /** Recorded keyframe ticks (scrubber markers / jump targets). */
  get keyframeTicks(): number[] {
    return this.replay.keyframes.map((k) => k.tick);
  }

  /** READ-ONLY view of the replay world (spectator camera / rendering). */
  get world(): ReplaySession['world'] {
    return this.session.world;
  }

  /** Current player pose in the replay. */
  playerPose(): ReturnType<ReplaySession['playerPose']> {
    return this.session.playerPose();
  }

  /** World-state fingerprint at the current tick. */
  hash(): string {
    return this.session.hash();
  }

  private buildStart(): ReplaySession {
    return ReplaySession.create(this.reg, {
      seed: this.replay.seed, options: this.replay.options, spawn: this.replay.spawn,
      snapshot: this.replay.startSnapshot,
    });
  }

  /** Verify (and, on a keyframe, checkpoint) the state at the current tick. */
  private assertKeyframe(tick: number): void {
    const expected = this.keyByTick.get(tick);
    if (expected === undefined) return;
    const actual = this.session.hash();
    if (actual !== expected) throw new ReplayDriftError(tick, expected, actual); // E-31
    if (!this.checkpoints.has(tick)) this.checkpoints.set(tick, this.session.snapshot());
  }

  /** Advance one tick (feed the next frame). Returns false at the end. */
  stepForward(): boolean {
    const i = this.head - this.replay.startTick;
    if (i >= this.replay.frames.length) return false;
    this.session.step(this.replay.frames[i]!);
    this.head++;
    this.assertKeyframe(this.head);
    return true;
  }

  /** Seek to absolute tick `t` (clamped). Forward = keep stepping; backward =
   *  restore the nearest checkpoint ≤ t, then step forward (REP-3 seek). */
  seek(t: number): void {
    const target = Math.max(this.startTick, Math.min(this.endTick, Math.floor(t)));
    if (target === this.head) return;
    if (target < this.head) {
      let anchor = this.startTick;
      for (const k of this.checkpoints.keys()) if (k <= target && k > anchor) anchor = k;
      this.session = ReplaySession.create(this.reg, {
        seed: this.replay.seed, options: this.replay.options, spawn: this.replay.spawn,
        snapshot: this.checkpoints.get(anchor)!,
      });
      this.head = anchor;
    }
    while (this.head < target) this.stepForward();
  }

  /** Jump to a recorded keyframe at or before `t` (scrubber "jump to keyframe"). */
  seekToKeyframe(t: number): void {
    let anchor = this.startTick;
    for (const tick of this.keyByTick.keys()) if (tick <= t && tick > anchor) anchor = tick;
    this.seek(anchor);
  }

  /** Re-sim the whole recording start→end, asserting every keyframe on the way
   *  (throws ReplayDriftError on the first divergence). Returns the final hash —
   *  the "re-sim hash == live hash" proof and the E2E verify entry point. */
  resimVerify(): string {
    this.seek(this.startTick);
    while (this.stepForward()) { /* assertKeyframe runs inside */ }
    return this.hash();
  }

  /** Timelapse (REP-4): re-sim start→end and invoke `onSample(tick)` every `everyN`
   *  ticks (and once at the end) so the caller can capture a render via the client
   *  screenshot hook. Pure here — the PNG capture is the injected callback. */
  runTimelapse(everyN: number, onSample: (tick: number) => void): void {
    const step = Math.max(1, Math.floor(everyN));
    this.seek(this.startTick);
    onSample(this.head);
    let sinceSample = 0;
    while (this.stepForward()) {
      if (++sinceSample >= step) { onSample(this.head); sinceSample = 0; }
    }
    if (sinceSample !== 0) onSample(this.head);
  }
}

/** Convenience: re-sim a recording to its final world-state hash (asserting every
 *  keyframe). Throws ReplayDriftError on drift (E-31). */
export function verifyReplay(reg: Registries, replay: Replay): string {
  return new ReplayPlayer(reg, replay).resimVerify();
}

/** Fold the definitive final hash of a replay by a straight forward re-sim (no
 *  keyframe map) — the ground truth a live recording's hash is compared against. */
export function replayFinalHash(reg: Registries, replay: Replay): string {
  const s = ReplaySession.create(reg, {
    seed: replay.seed, options: replay.options, spawn: replay.spawn, snapshot: replay.startSnapshot,
  });
  for (const f of replay.frames) s.step(f);
  return hashWorld(s.world);
}
